import { LaunchItem } from "types/api/launches/getLaunches.ts";
import { UntitledLaunchText } from "../ProductLaunchesPage.text.ts";

const MONTH_NAMES = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December"
];

const cleanText = (value: string | null | undefined) => value?.trim() || undefined;

/** Only http(s) urls are rendered as links or images, anything else coming from the API is ignored. */
export const getSafeUrl = (value: string | null | undefined) => {
	const url = cleanText(value);
	if (!url) return undefined;

	try {
		const { protocol } = new URL(url);
		return protocol === "http:" || protocol === "https:" ? url : undefined;
	} catch {
		return undefined;
	}
};

/** 2026-02-10 -> February 10, 2026 (reads the calendar date as-is, so no timezone shifting occurs) */
export const formatLaunchDate = (value: string | null | undefined) => {
	const dateText = cleanText(value);
	if (!dateText) return undefined;

	const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateText);
	if (isoDateMatch) {
		const [, year, month, day] = isoDateMatch;
		const monthName = MONTH_NAMES[Number(month) - 1];
		const dayOfMonth = Number(day);

		return monthName && dayOfMonth >= 1 && dayOfMonth <= 31 ? `${monthName} ${dayOfMonth}, ${year}` : undefined;
	}

	const date = new Date(dateText);
	if (Number.isNaN(date.getTime())) return undefined;

	return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

/** yyyy-MM-dd -> comparable number (20260210). Missing or invalid dates return undefined. */
const getEventStartDateSortValue = (value: string | null | undefined) => {
	const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(cleanText(value) ?? "");
	if (!isoDateMatch) return undefined;

	const [, year, month, day] = isoDateMatch;
	return Number(year) * 10000 + Number(month) * 100 + Number(day);
};

/**
 * Reverse chronological order by `eventStartDate` (newest first). Launches without a valid date go last and
 * keep the order the API returned them in. Returns a new array, the input is not mutated.
 */
export const sortLaunchesByEventStartDateDesc = (launches: LaunchItem[]) =>
	[...launches].sort((first, second) => {
		const firstValue = getEventStartDateSortValue(first.eventStartDate);
		const secondValue = getEventStartDateSortValue(second.eventStartDate);

		if (firstValue === undefined && secondValue === undefined) return 0;
		if (firstValue === undefined) return 1;
		if (secondValue === undefined) return -1;

		return secondValue - firstValue;
	});

/** `subject` is the card heading. `name` is an internal id and is intentionally never displayed. */
export const getLaunchHeading = (launch: LaunchItem) => cleanText(launch.subject) ?? UntitledLaunchText;

/** `subtitle` is the optional line under the heading. */
export const getLaunchSubtitle = (launch: LaunchItem) => cleanText(launch.subtitle);
